import { useState } from "react"
import { Outlet } from "react-router-dom"
import { Navbar } from "./navbar"
import { Footer } from "./footer"
import { Sidebar } from "./sidebar"
import { BigHexagon } from "@/components/custom/decoration/big-hexagon"
import { useAuth } from "@/context/auth/auth-context"
import { Sheet, SheetContent } from "@/components/ui/sheet"

export function MainLayout() {
	const { isAuthenticated } = useAuth()
	const [mobileOpen, setMobileOpen] = useState(false)

	return (
		<div className="flex flex-col h-screen overflow-hidden font-sans selection:bg-sky-500/30 selection:text-sky-900 dark:selection:text-sky-50 relative">
			{/* --- GLOBAL BACKGROUND (Fixed) --- */}
			<div
				className="fixed inset-0 -z-50
        bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))]
        from-[#f0f9ff] via-[#e0f2fe] to-[#bae6fd]
        dark:from-sky-950/30 dark:via-background dark:to-background
        transition-colors duration-500"
			></div>

			{/* --- DECORATIVE HEXAGONS (Fixed) --- */}
			<div className="fixed inset-0 -z-40 overflow-hidden pointer-events-none">
				{/* Layer 1: Far/Blurry */}
				<BigHexagon className="absolute top-10 left-[10%] w-16 h-16 text-sky-600/10 dark:text-sky-500/5 blur-[2px] animate-pulse-slow" />
				<BigHexagon className="absolute top-20 right-[15%] w-12 h-12 text-sky-600/10 dark:text-sky-500/5 blur-[1px]" />
				<BigHexagon className="absolute bottom-32 left-[20%] w-24 h-24 text-sky-600/5 dark:text-sky-500/5 blur-[3px]" />

				{/* Layer 2: Mid/Sharp */}
				<div className="absolute top-1/4 left-[-5%] md:left-[5%] opacity-40 dark:opacity-20 transition-opacity duration-500">
					<BigHexagon className="w-48 h-48 text-sky-600/30 dark:text-sky-500/10" />
					<BigHexagon className="w-32 h-32 text-sky-600/20 dark:text-sky-500/5 absolute -bottom-16 -right-12" />
				</div>

				<div className="absolute bottom-1/4 right-[-5%] md:right-[5%] opacity-40 dark:opacity-20 transition-opacity duration-500">
					<BigHexagon className="w-56 h-56 text-sky-600/30 dark:text-sky-500/10" />
					<BigHexagon className="w-24 h-24 text-sky-600/20 dark:text-sky-500/5 absolute -top-12 -left-12" />
				</div>

				{/* Layer 3: Foreground/Blurry */}
				<BigHexagon className="absolute -bottom-12 -left-12 w-64 h-64 text-sky-700/20 dark:text-sky-500/10 blur-md opacity-20 dark:opacity-10" />
				<BigHexagon className="absolute top-1/3 right-0 translate-x-1/2 w-96 h-96 text-sky-700/10 dark:text-sky-500/5 blur-2xl opacity-20 dark:opacity-10" />
			</div>

			{/* --- PAGE STRUCTURE --- */}
			<Navbar onMenuClick={() => setMobileOpen(true)} />

			{/* Content row: sidebar + scrollable main. overflow-hidden keeps the
			    sidebar pinned while only the main area scrolls. */}
			<div className="flex flex-1 overflow-hidden">
				{/* Desktop sidebar — always visible on md+, fills full remaining height */}
				{isAuthenticated && (
					<aside className="hidden md:flex shrink-0 flex-col overflow-y-auto">
						<Sidebar />
					</aside>
				)}

				{/* Mobile sidebar — Sheet drawer */}
				{isAuthenticated && (
					<Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
						<SheetContent side="left" className="p-0 w-56 sm:max-w-56">
							<Sidebar mobile onNavigate={() => setMobileOpen(false)} />
						</SheetContent>
					</Sheet>
				)}

				{/* Main content — this is the only thing that scrolls */}
				<main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
					<Outlet />
					<Footer />
				</main>
			</div>
		</div>
	)
}
